import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface AuroraInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const AuroraInsights: React.FC<AuroraInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Bullet text entrance animation
  const textOpacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const textScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 14, stiffness: 100, mass: 0.6 },
  });

  // Card entrance animation on first bullet
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 12], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { damping: 15, mass: 0.7, stiffness: 90 },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "rgba(13,31,26,0.88)",
          border: "1.5px solid",
          borderImageSlice: 1,
          borderImageSource: "linear-gradient(135deg, #50fa7b, #bd93f9)",
          borderRadius: "12px",
          boxShadow: "0 0 40px rgba(80,250,123,0.15)",
          padding: "40px",
          display: "flex",
          flexDirection: "row",
          gap: "24px",
          position: "relative",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
        }}
      >
        {/* Left decorative gradient bar */}
        <div
          style={{
            width: "3px",
            alignSelf: "stretch",
            flexShrink: 0,
            background: "linear-gradient(180deg, #50fa7b, #bd93f9)",
            borderRadius: "2px",
          }}
        />

        {/* Main content */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            flex: 1,
          }}
        >
          {/* Dot pagination + brand badge */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              {bullets.map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: i === activeIndex ? 20 : 6,
                    height: 6,
                    borderRadius: 3,
                    background:
                      i === activeIndex ? "#50fa7b" : "rgba(189,147,249,0.3)",
                    boxShadow:
                      i === activeIndex ? "0 0 8px rgba(80,250,123,0.6)" : "none",
                  }}
                />
              ))}
            </div>
            {(() => {
              const brand = detectBrandInText(activeBullet.text);
              return brand ? (
                <CompanyBadge
                  name={brand}
                  color="#50fa7b"
                  accentColor="#50fa7b"
                  size={68}
                  style="glow"
                  opacity={textOpacity}
                />
              ) : null;
            })()}
          </div>

          {/* Bullet text with entrance animation */}
          <div
            style={{
              opacity: textOpacity,
              transform: `scale(${textScale})`,
              minHeight: "280px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <p
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "52px",
                fontWeight: 600,
                fontStyle: "italic",
                lineHeight: 1.4,
                color: "#e8fff5",
                textAlign: "left",
                margin: 0,
              }}
            >
              <HighlightText
                highlightStyle={{
                  fontWeight: 700,
                  color: "#50fa7b",
                  textShadow: "0 0 15px rgba(80,250,123,0.7)",
                  display: "inline",
                }}
                text={activeBullet.text}
                highlights={activeBullet.highlights}
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
